import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Button, Card, EmptyState, Skeleton } from '@/ui/primitives'
import { CardZbor } from './zbor'
import { EditorProgram } from './program-editor'

type Eveniment = { tip: string; titlu: string; detaliu: string | null; ora: string | null }
type Zi = { data: string | null; zi: number; eticheta: string; evenimente: Eveniment[] } & ZiText
type ZiText = { titlu?: string | null; text?: string | null }
type Program = {
  personalizat?: boolean
  titlu?: string | null
  intro?: string | null
  note?: string | null
  sursa?: string | null
  actualizat_de?: string | null
  reservation: {
    id: number; number: string; partner: string | null; booking_customer: string | null
    destination: string | null; hotel: string | null; room_type: string | null
    meal_type: string | null; begin_date: string; end_date: string; nights: number
    adults: number | null; children: number | null; responsible: string | null
    remarks: string | null; suppliers: string | null
  }
  turisti: string[]
  servicii: { tip: string; descriere: string | null; begin_date: string | null; furnizor: string | null }[]
  zile: Zi[]
  lipsesc: string[]
}

const PUNCT: Record<string, string> = {
  zbor: 'bg-accent', plecare: 'bg-accent', retur: 'bg-accent',
  cazare: 'bg-good', serviciu: 'bg-warn',
}

const ziLunga = (s: string) =>
  new Date(s).toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })

/** Programul calatoriei, pe zile. Se tipareste si se da clientului. */
export function Itinerar() {
  const { id = '' } = useParams()
  const [pdf, setPdf] = useState<'gata' | 'lucreaza' | string>('gata')
  const [editez, setEditez] = useState(false)
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['itinerar', id], queryFn: () => api.get<Program>(`/reservations/${id}/itinerary`),
    retry: false,
  })

  if (isPending) return (<><PageHeader title="Program" /><div className="px-8 py-6"><Card><Skeleton /></Card></div></>)
  if (isError || !data?.reservation) return (
    <><PageHeader title="Program" /><div className="px-8 py-6"><Card>
      <EmptyState title="Nu am putut face programul"
        hint={error instanceof Error ? error.message : undefined} /></Card></div></>)

  const r = data.reservation

  return (
    <>
      <PageHeader
        title={data.titlu || `Program · rezervarea ${r.number}`}
        count={data.personalizat
          ? `${data.zile.length} zile · scris de om${data.sursa === 'touroperator' ? ', de la touroperator' : ''}`
          : `${r.nights} nopti · ${data.zile.length} zile`}
        actions={
          <>
            <Button
              onClick={async () => {
                setPdf('lucreaza')
                try { await api.deschideFisier(`/documents/${id}/program/pdf`); setPdf('gata') }
                catch (e) { setPdf(e instanceof Error ? e.message : 'Nu s-a putut genera') }
              }}
              disabled={pdf === 'lucreaza'}>
              {pdf === 'lucreaza' ? 'Se genereaza…' : 'PDF'}
            </Button>
            <Button onClick={() => setEditez((v) => !v)}>
              {editez ? 'Inchide editorul' : 'Editeaza programul'}
            </Button>
            <Button onClick={() => window.print()}>Tipareste</Button>
            <Link to={`/rezervari/${r.id}`} className="text-[14px] text-muted hover:text-ink">← Fisa rezervarii</Link>
            {pdf !== 'gata' && pdf !== 'lucreaza' && <span className="text-[13px] text-bad">{pdf}</span>}
          </>
        }
      />
      <div className="px-8 py-6 grid gap-6 lg:grid-cols-[1fr_300px] items-start">
        <div className="min-w-0 space-y-4">
          {editez && <div className="fara-tipar"><EditorProgram id={id} inchide={() => setEditez(false)} /></div>}

          {data.intro && <p className="text-[14.5px] whitespace-pre-line">{data.intro}</p>}

          {data.lipsesc.length > 0 && (
            <Card className="p-4 bg-warn-soft border-warn/30 fara-tipar">
              <p className="text-[13.5px] text-warn">
                Nu sunt trecute in rezervare: {data.lipsesc.join(', ')}. Programul le arata gol,
                nu le inventeaza.
              </p>
            </Card>
          )}

          <div className="fara-tipar">
            <CardZbor reservationId={r.id} deschisImplicit={data.lipsesc.some((x) => x.includes('zbor'))} />
          </div>

          <Card className="p-0 overflow-hidden">
            {data.zile.map((z, i) => (
              <div key={z.data} className={'flex gap-5 px-6 py-4 ' + (i > 0 ? 'border-t border-line' : '')}>
                <div className="w-32 shrink-0">
                  <p className="text-[12.5px] uppercase tracking-[0.08em] text-muted">{z.eticheta}</p>
                  {z.data && <p className="text-[14px] mt-0.5">{ziLunga(z.data)}</p>}
                </div>
                <div className="flex-1 min-w-0 space-y-2.5">
                  {(z.titlu || z.text) ? (
                    <div>
                      {z.titlu && <p className="text-[14.5px] font-medium">{z.titlu}</p>}
                      {z.text && <p className="text-[14px] text-ink-soft whitespace-pre-line">{z.text}</p>}
                    </div>
                  ) : z.evenimente.length === 0 ? (
                    <p className="text-[14px] text-muted">Zi libera</p>
                  ) : null}
                  {z.evenimente.map((e, j) => (
                    <div key={j} className="flex gap-3">
                      <span className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${PUNCT[e.tip] ?? 'bg-line-strong'}`} aria-hidden />
                      <div className="min-w-0">
                        <p className="text-[14.5px]">
                          {e.ora && <span className="tnum text-muted mr-2">{e.ora}</span>}
                          {e.titlu}
                        </p>
                        {e.detaliu && <p className="text-[13px] text-muted">{e.detaliu}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Card>

          {data.note && (
            <Card className="p-4">
              <p className="text-[13.5px] whitespace-pre-line">{data.note}</p>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-5 space-y-2">
            <p className="text-[12.5px] text-muted">Calatoria</p>
            <p className="text-[14.5px] font-medium">{r.partner ?? r.booking_customer ?? '—'}</p>
            <p className="text-[13.5px]">{[r.destination, r.hotel].filter(Boolean).join(' · ') || '—'}</p>
            <p className="text-[13px] text-muted">{[r.room_type, r.meal_type].filter(Boolean).join(' · ')}</p>
            <p className="text-[13px] text-muted tnum">
              {(r.adults ?? 0)} adulti{r.children ? `, ${r.children} copii` : ''}
            </p>
            {r.suppliers && <p className="text-[13px] text-muted">furnizor: {r.suppliers}</p>}
          </Card>

          {data.turisti.length > 0 && (
            <Card className="p-5">
              <p className="text-[12.5px] text-muted mb-2">Turisti ({data.turisti.length})</p>
              <ul className="space-y-1 text-[13.5px]">
                {data.turisti.map((t, i) => <li key={i} className="truncate">{t}</li>)}
              </ul>
            </Card>
          )}

          {data.servicii.length > 0 && (
            <Card className="p-5">
              <p className="text-[12.5px] text-muted mb-2">Servicii ({data.servicii.length})</p>
              <ul className="space-y-2 text-[13.5px]">
                {data.servicii.map((s, i) => (
                  <li key={i}>
                    <span className="block">{s.descriere ?? s.tip}</span>
                    {s.furnizor && <span className="block text-[12.5px] text-muted">{s.furnizor}</span>}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {r.remarks && (
            <Card className="p-5">
              <p className="text-[12.5px] text-muted mb-1">Observatii</p>
              <p className="text-[13.5px] whitespace-pre-line">{r.remarks}</p>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
